<script>
  import { createEventDispatcher } from 'svelte';
  import { _ } from '../lib/i18n.js';
  import { X, Edit2, Plus, AlertCircle, Info } from 'lucide-svelte';

  export let customer = null;
  // Save failure reported by the parent, shaped { status, message }. Shown in the
  // modal because the page banner renders behind the backdrop.
  export let serverError = null;

  const dispatch = createEventDispatcher();
  const isEdit = !!customer;
  const hasPortalAccount = isEdit && customer?.hasPortalAccount === true;

  const SERVER_ERROR_TITLE_KEYS = {
    400: 'customers.modal.serverError.badRequest',
    403: 'customers.modal.serverError.forbidden',
    409: 'customers.modal.serverError.conflict',
    503: 'customers.modal.serverError.unavailable'
  };

  $: serverErrorTitle = serverError
    ? $_(SERVER_ERROR_TITLE_KEYS[serverError.status] || 'customers.modal.serverError.generic')
    : '';

  let formData = {
    id: '',
    externalRefAruba: '',
    type: '',
    destinationCode: '',
    email: '',
    pec: '',
    phone: '',
    countryCode: '',
    VATNumber: '',
    fiscalCode: '',
    companyName: '',
    name: '',
    surname: '',
    EORICode: '',
    country: '',
    cap: '',
    province: '',
    municipality: '',
    address: '',
    houseNumber: '',
    beneficiary: '',
    paymentTerms: '',
    paymentMethod: '',
    bank: '',
    discountPercentage: 0,
    isPublicAdministration: false
  };

  let errors = {};
  let loading = false;

  // Initialize form data
  if (customer) {
    formData = { ...formData, ...customer };
  }

  function handleSubmit() {
    errors = {};

    if (!formData.companyName && !formData.name && !formData.surname) {
      errors.companyName = $_('customers.validation.nameRequired');
    }

    if (formData.email && !isValidEmail(formData.email)) {
      errors.email = $_('customers.validation.emailInvalid');
    }

    if (formData.pec && !isValidEmail(formData.pec)) {
      errors.pec = $_('customers.validation.pecInvalid');
    }

    // Validate discount percentage (0-100)
    const discount = parseFloat(formData.discountPercentage);
    if (isNaN(discount) || discount < 0 || discount > 100) {
      errors.discountPercentage = $_('customers.validation.discountInvalid');
    }

    if (Object.keys(errors).length > 0) {
      return;
    }

    // Clean up empty strings to null, preserve non-string values
    const cleanedData = {};
    for (const [key, value] of Object.entries(formData)) {
      // The API generates the primary ID. Keep it in edit payloads for the
      // existing page contract, but never send an ID when creating a customer.
      if (key === 'id' && !isEdit) continue;
      if (typeof value === 'string') {
        cleanedData[key] = value.trim() === '' ? null : value.trim();
      } else {
        cleanedData[key] = value;
      }
    }

    dispatch('save', {
      customer: cleanedData,
      isEdit
    });
  }

  function handleCancel() {
    dispatch('cancel');
  }

  function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      handleCancel();
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- Modal backdrop -->
<div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 fade-in">
  <!-- Modal -->
  <div class="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/60 dark:border-gray-700/60 max-w-4xl w-full max-h-[90vh] overflow-hidden scale-in flex flex-col">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between bg-linear-to-r from-primary-50/50 dark:from-primary-900/20 to-transparent">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
          {#if isEdit}
            <Edit2 size={20} class="text-white" />
          {:else}
            <Plus size={20} class="text-white" />
          {/if}
        </div>
        <div>
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
            {isEdit ? $_('customers.edit') : $_('customers.add')}
          </h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {isEdit ? $_('customers.modal.editSubtitle') : $_('customers.modal.addSubtitle')}
          </p>
        </div>
      </div>
      <button
        on:click={handleCancel}
        class="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
      >
        <X size={24} />
      </button>
    </div>

    {#if serverError}
      <!-- Outside the scrolling content so it is visible whatever the scroll position -->
      <div
        class="mx-6 mt-4 flex items-start space-x-3 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4 flex-shrink-0"
        role="alert"
        data-testid="customer-modal-server-error"
      >
        <AlertCircle size={20} class="mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400" />
        <div class="min-w-0">
          <p class="text-sm font-semibold text-red-800 dark:text-red-300">{serverErrorTitle}</p>
          {#if serverError.message}
            <p class="mt-1 text-sm text-red-700 dark:text-red-400 break-words">{serverError.message}</p>
          {/if}
        </div>
      </div>
    {/if}

    <!-- Content -->
    <div class="p-6 overflow-y-auto flex-1">
      <form on:submit|preventDefault={handleSubmit} class="space-y-6">
        <!-- Basic Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          {#if isEdit}
            <div>
              <label for="id" class="label">{$_('customers.fields.id')}</label>
              <input
                id="id"
                type="text"
                value={formData.id}
                readonly
                aria-readonly="true"
                class="input cursor-not-allowed bg-gray-50 dark:bg-gray-800"
                data-testid="customer-generated-id"
              />
            </div>
          {/if}

          <div>
            <label for="externalRefAruba" class="label">{$_('customers.fields.externalRefAruba')}</label>
            <input
              id="externalRefAruba"
              type="text"
              bind:value={formData.externalRefAruba}
              maxlength="100"
              class="input"
            />
          </div>

          <div>
            <label for="type" class="label">{$_('customers.fields.type')}</label>
            <input
              id="type"
              type="text"
              bind:value={formData.type}
              class="input"
            />
          </div>
        </div>

        <!-- Company/Person Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="companyName" class="label">
              {$_('customers.fields.companyName')} *
            </label>
            <input
              id="companyName"
              type="text"
              bind:value={formData.companyName}
              class="input {errors.companyName ? 'border-red-500' : ''}"
            />
            {#if errors.companyName}
              <p class="text-red-600 dark:text-red-400 text-sm mt-1">{errors.companyName}</p>
            {/if}
          </div>

          <div class="grid grid-cols-2 gap-2">
            <div>
              <label for="name" class="label">{$_('customers.fields.name')}</label>
              <input
                id="name"
                type="text"
                bind:value={formData.name}
                class="input"
              />
            </div>
            <div>
              <label for="surname" class="label">{$_('customers.fields.surname')}</label>
              <input
                id="surname"
                type="text"
                bind:value={formData.surname}
                class="input"
              />
            </div>
          </div>
        </div>

        <!-- Contact Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="email" class="label">{$_('customers.fields.email')}</label>
            <input
              id="email"
              type="email"
              bind:value={formData.email}
              class="input {errors.email ? 'border-red-500' : ''}"
            />
            {#if errors.email}
              <p class="text-red-600 dark:text-red-400 text-sm mt-1">{errors.email}</p>
            {/if}
            {#if hasPortalAccount}
              <p class="mt-1 flex items-start space-x-1.5 text-xs text-gray-500 dark:text-gray-400" data-testid="customer-modal-portal-email-hint">
                <Info size={14} class="mt-px flex-shrink-0 text-primary-500 dark:text-primary-400" />
                <span>{$_('customers.modal.portalEmailHint')}</span>
              </p>
            {/if}
          </div>

          <div>
            <label for="pec" class="label">{$_('customers.fields.pec')}</label>
            <input
              id="pec"
              type="email"
              bind:value={formData.pec}
              class="input {errors.pec ? 'border-red-500' : ''}"
            />
            {#if errors.pec}
              <p class="text-red-600 dark:text-red-400 text-sm mt-1">{errors.pec}</p>
            {/if}
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="phone" class="label">{$_('customers.fields.phone')}</label>
            <input
              id="phone"
              type="tel"
              bind:value={formData.phone}
              class="input"
            />
          </div>

          <div>
            <label for="destinationCode" class="label">
              {$_('customers.fields.destinationCode')}
            </label>
            <input
              id="destinationCode"
              type="text"
              bind:value={formData.destinationCode}
              maxlength="7"
              class="input"
            />
          </div>
        </div>

        <!-- Fiscal Info -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label for="countryCode" class="label">{$_('customers.fields.countryCode')}</label>
            <input
              id="countryCode"
              type="text"
              bind:value={formData.countryCode}
              maxlength="2"
              class="input"
            />
          </div>

          <div>
            <label for="VATNumber" class="label">{$_('customers.fields.VATNumber')}</label>
            <input
              id="VATNumber"
              type="text"
              bind:value={formData.VATNumber}
              class="input"
            />
          </div>

          <div>
            <label for="fiscalCode" class="label">{$_('customers.fields.fiscalCode')}</label>
            <input
              id="fiscalCode"
              type="text"
              bind:value={formData.fiscalCode}
              maxlength="16"
              class="input"
            />
          </div>
        </div>

        <div>
          <label for="EORICode" class="label">{$_('customers.fields.EORICode')}</label>
          <input
            id="EORICode"
            type="text"
            bind:value={formData.EORICode}
            class="input"
          />
        </div>

        <!-- Address -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="country" class="label">{$_('customers.fields.country')}</label>
            <input
              id="country"
              type="text"
              bind:value={formData.country}
              class="input"
            />
          </div>

          <div class="grid grid-cols-2 gap-2">
            <div>
              <label for="cap" class="label">{$_('customers.fields.cap')}</label>
              <input
                id="cap"
                type="text"
                bind:value={formData.cap}
                class="input"
              />
            </div>
            <div>
              <label for="province" class="label">{$_('customers.fields.province')}</label>
              <input
                id="province"
                type="text"
                bind:value={formData.province}
                class="input"
              />
            </div>
          </div>
        </div>

        <div>
          <label for="municipality" class="label">{$_('customers.fields.municipality')}</label>
          <input
            id="municipality"
            type="text"
            bind:value={formData.municipality}
            class="input"
          />
        </div>

        <div class="grid grid-cols-3 gap-4">
          <div class="col-span-2">
            <label for="address" class="label">{$_('customers.fields.address')}</label>
            <input
              id="address"
              type="text"
              bind:value={formData.address}
              class="input"
            />
          </div>
          <div>
            <label for="houseNumber" class="label">{$_('customers.fields.houseNumber')}</label>
            <input
              id="houseNumber"
              type="text"
              bind:value={formData.houseNumber}
              class="input"
            />
          </div>
        </div>

        <!-- Payment Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="beneficiary" class="label">{$_('customers.fields.beneficiary')}</label>
            <input
              id="beneficiary"
              type="text"
              bind:value={formData.beneficiary}
              class="input"
            />
          </div>

          <div>
            <label for="paymentTerms" class="label">{$_('customers.fields.paymentTerms')}</label>
            <input
              id="paymentTerms"
              type="text"
              bind:value={formData.paymentTerms}
              class="input"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="paymentMethod" class="label">{$_('customers.fields.paymentMethod')}</label>
            <input
              id="paymentMethod"
              type="text"
              bind:value={formData.paymentMethod}
              class="input"
            />
          </div>

          <div>
            <label for="bank" class="label">{$_('customers.fields.bank')}</label>
            <input
              id="bank"
              type="text"
              bind:value={formData.bank}
              class="input"
            />
          </div>
        </div>

        <!-- Commercial Terms -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label for="discountPercentage" class="label">{$_('customers.fields.discountPercentage')}</label>
            <div class="relative">
              <input
                id="discountPercentage"
                type="number"
                step="0.01"
                min="0"
                max="100"
                bind:value={formData.discountPercentage}
                class="input pr-8 {errors.discountPercentage ? 'border-red-500' : ''}"
              />
              <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">%</span>
            </div>
            {#if errors.discountPercentage}
              <p class="text-red-600 dark:text-red-400 text-sm mt-1">{errors.discountPercentage}</p>
            {/if}
          </div>

          <div class="flex items-start space-x-3 md:pt-7">
            <input
              id="isPublicAdministration"
              type="checkbox"
              bind:checked={formData.isPublicAdministration}
              class="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500"
            />
            <label for="isPublicAdministration" class="cursor-pointer">
              <span class="label mb-0">{$_('customers.fields.isPublicAdministration')}</span>
              <span class="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                {$_('customers.fields.isPublicAdministrationHint')}
              </span>
            </label>
          </div>
        </div>
      </form>
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 flex-shrink-0">
      <button
        type="button"
        on:click={handleCancel}
        class="btn btn-secondary"
        disabled={loading}
      >
        {$_('common.cancel')}
      </button>
      <button
        type="submit"
        on:click={handleSubmit}
        class="btn btn-primary"
        disabled={loading}
      >
        {#if loading}
          <svg class="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        {/if}
        {$_('common.save')}
      </button>
    </div>
  </div>
</div>
